import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { CalendarDays, ChevronDown, PenTool, Zap, BarChart3, RefreshCw, Home } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { FollowUp, LeadCounts, ActiveArea } from "@/hooks/useDashboardData";
import { LeadCards } from "./LeadCards";

interface DashboardHeaderProps {
  currentTime: Date;
  todayFollowUps: FollowUp[];
  globalLeadCounts: LeadCounts;
  isLoadingLeadCounts?: boolean;
  onOpenCalendar?: () => void;
  onOpenStatistics?: () => void;
  onReorganizeFollowups?: () => void;
  isReorganizingFollowups?: boolean;
  /** If true, clicking a lead card navigates to /running-list. If false, calls onLeadCardClick */
  navigateOnLeadClick?: boolean;
  onLeadCardClick?: (type: 'hot' | 'followup' | 'na') => void;
  selectedLeadType?: 'hot' | 'followup' | 'na' | null;
  /** If true, clicking a follow-up navigates to the unit. If false, navigates to /running-list */
  enableUnitNavigation?: boolean;
  onNavigateToUnit?: (areaId: string, streetId: string, houseId: string, unitId: string) => Promise<void>;
  /** Show additional buttons like statistics and reorganize */
  showExtendedControls?: boolean;
  /** Active area to display */
  activeArea?: { id: string; name: string; plz?: string | null; is_active?: boolean } | null;
  /** Show the active area button */
  showActiveAreaButton?: boolean;
  /** Callback when clicking the active area button (if not provided, navigates to /running-list) */
  onActiveAreaClick?: () => void;
  /** Callback when clicking the home icon (navigate to area overview) */
  onHomeIconClick?: () => void;
  /** Selected lead type label to show in the area button */
  selectedLeadTypeLabel?: string;
}

export const DashboardHeader = ({
  currentTime,
  todayFollowUps,
  globalLeadCounts,
  isLoadingLeadCounts = false,
  onOpenCalendar,
  onOpenStatistics,
  onReorganizeFollowups,
  isReorganizingFollowups = false,
  navigateOnLeadClick = true,
  onLeadCardClick,
  selectedLeadType,
  enableUnitNavigation = false,
  onNavigateToUnit,
  showExtendedControls = false,
  activeArea,
  showActiveAreaButton = false,
  onActiveAreaClick,
  onHomeIconClick,
  selectedLeadTypeLabel
}: DashboardHeaderProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isTimelineOpen, setIsTimelineOpen] = useState(true);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const followUpListRef = useRef<HTMLDivElement>(null);

  // Center timeline on current hour
  useEffect(() => {
    if (!timelineScrollRef.current) return;
    const currentHour = currentTime.getHours();
    const scrollContainer = timelineScrollRef.current;
    const itemWidth = 88;
    const containerWidth = scrollContainer.clientWidth;
    const currentHourIndex = Math.max(0, currentHour - 9);
    const scrollLeft = currentHourIndex * itemWidth - containerWidth / 2 + itemWidth / 2;
    scrollContainer.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });
  }, [currentTime]);

  const handleLeadCardClick = useCallback((type: 'hot' | 'followup' | 'na') => {
    if (navigateOnLeadClick) {
      navigate('/running-list', { state: { selectedLeadType: type } });
    } else if (onLeadCardClick) {
      onLeadCardClick(type);
    }
  }, [navigateOnLeadClick, navigate, onLeadCardClick]);

  const handleFollowUpClick = async (followUp: FollowUp) => {
    if (followUp.source === 'd2d_units') {
      if (enableUnitNavigation && onNavigateToUnit && user) {
        const unitId = followUp.id.replace('d2d_', '');
        try {
          const { data: unitData, error } = await supabase
            .from('d2d_units')
            .select(`
              id,
              d2d_houses!inner(
                id,
                d2d_streets!inner(
                  id,
                  d2d_areas!inner(id)
                )
              )
            `)
            .eq('id', unitId)
            .eq('user_id', user.id)
            .maybeSingle();

          if (error) throw error;

          if (unitData) {
            const areaId = unitData.d2d_houses.d2d_streets.d2d_areas.id;
            const streetId = unitData.d2d_houses.d2d_streets.id;
            const houseId = unitData.d2d_houses.id;
            await onNavigateToUnit(areaId, streetId, houseId, unitId);
          } else {
            toast({
              title: "Navigation Fehler",
              description: "Wohneinheit nicht gefunden.",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('Error navigating to unit:', error);
          toast({
            title: "Navigation Fehler",
            description: "Fehler beim Navigieren zur Wohneinheit.",
            variant: "destructive",
          });
        }
      } else {
        navigate('/running-list');
      }
    } else {
      toast({
        title: "Info",
        description: "Diese Funktion ist nur für D2D-Einträge verfügbar.",
      });
    }
  };

  const shortenAddress = (text: string | undefined, maxLength: number = 15) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-4">
      {/* Digital Clock */}
      <div className="text-center">
        <div className="inline-flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 border-2 border-primary/20 rounded-2xl px-4 sm:px-8 py-4 shadow-lg backdrop-blur-sm">
          <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider text-center">
            {currentTime.toLocaleDateString('de-DE', { weekday: 'long' })} {currentTime.toLocaleDateString('de-DE', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}
          </div>
          <div className="text-2xl sm:text-4xl font-mono font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent tracking-tight">
            {currentTime.toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false
            })}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <Collapsible open={isTimelineOpen} onOpenChange={setIsTimelineOpen}>
        <div className="bg-card border rounded-lg">
          <CollapsibleTrigger className="w-full p-4 hover:bg-muted/20 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-muted-foreground">Heutiger Zeitplan</h3>
                {onOpenCalendar && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenCalendar();
                    }}
                    className="p-1.5 hover:bg-muted rounded-md transition-colors"
                    title="Kalenderansicht"
                  >
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  </button>
                )}
                {showExtendedControls && onOpenStatistics && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenStatistics();
                    }}
                    className="p-1.5 hover:bg-muted rounded-md transition-colors"
                    title="Erfolgsstatistik"
                  >
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </button>
                )}
                {showExtendedControls && onReorganizeFollowups && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onReorganizeFollowups();
                    }}
                    disabled={true}
                    className="p-1.5 hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Auto-Reschedule (deaktiviert)"
                  >
                    <RefreshCw className={cn("h-4 w-4 text-muted-foreground", isReorganizingFollowups && "animate-spin")} />
                  </button>
                )}
              </div>
              <ChevronDown className={cn("h-4 w-4 transition-transform", isTimelineOpen && "rotate-180")} />
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="px-4 pb-4">
            <div className="relative">
              {/* Timeline visual */}
              <div className="mb-4">
                <div ref={timelineScrollRef} className="overflow-x-auto pb-2">
                  <div className="flex gap-6 px-4 min-w-max">
                    {Array.from({ length: 13 }, (_, i) => i + 9).map(hour => {
                      const hourFollowUps = todayFollowUps.filter(followUp => {
                        const [hours] = followUp.time.split(':').map(Number);
                        return hours === hour;
                      });

                      return (
                        <div key={hour} className="flex flex-col items-center min-w-16">
                          <div className="text-xs text-muted-foreground mb-2 font-medium">
                            {hour.toString().padStart(2, '0')}:00
                          </div>
                          <div className="relative h-16 w-6 bg-muted/20 rounded-full flex flex-col-reverse items-center justify-start gap-1 py-1">
                            {hourFollowUps.map((followUp) => {
                              const [followUpHour, followUpMinute] = followUp.time.split(':').map(Number);
                              const followUpTime = new Date(currentTime);
                              followUpTime.setHours(followUpHour, followUpMinute, 0, 0);
                              const isMissed = currentTime > followUpTime;

                              return (
                                <div
                                  key={followUp.id}
                                  className={`w-2 h-8 rounded-sm cursor-pointer transition-all duration-200 hover:w-3 ${isMissed
                                    ? 'bg-gray-400 hover:bg-gray-500'
                                    : followUp.isHot
                                      ? 'bg-orange-500 hover:bg-orange-600'
                                      : followUp.isKT
                                        ? 'bg-blue-500 hover:bg-blue-600'
                                        : 'bg-red-500 hover:bg-red-600'
                                    }`}
                                  title={`${followUp.time} - ${followUp.name} ${isMissed ? '(Verpasst)' : ''} ${followUp.isHot ? '🔥' : followUp.isKT ? '👤' : ''}`}
                                  onClick={() => handleFollowUpClick(followUp)}
                                />
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Follow-up list */}
              {todayFollowUps.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground">Heutige Follow-Ups:</h4>
                  <div ref={followUpListRef} className="space-y-1 max-h-32 overflow-y-auto overflow-x-hidden">
                    {todayFollowUps.map((followUp) => (
                      <div
                        key={followUp.id}
                        className={`flex flex-col gap-1 text-xs rounded px-2 py-1 cursor-pointer transition-colors ${followUp.isHot
                          ? 'bg-destructive/20 text-destructive-foreground border border-destructive/30 hover:bg-destructive/30'
                          : followUp.isKT
                            ? 'bg-[hsl(var(--d2d-kt)/0.1)] text-[hsl(var(--d2d-kt))] border border-[hsl(var(--d2d-kt)/0.3)] hover:bg-[hsl(var(--d2d-kt)/0.2)]'
                            : 'bg-muted/20 hover:bg-muted/30'
                          }`}
                        onClick={() => handleFollowUpClick(followUp)}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            {followUp.manual_reminder ? (
                              <PenTool className="h-3 w-3 text-blue-500" />
                            ) : (
                              <Zap className="h-3 w-3 text-green-500" />
                            )}
                            <span className="font-mono font-medium text-xs">{followUp.time}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {followUp.isHot && <span className="text-orange-500">🔥</span>}
                            {followUp.isKT && <span className="text-blue-500">👤</span>}
                            <span className="text-muted-foreground truncate text-xs">{shortenAddress(followUp.name || followUp.address)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Active Area Button */}
      {showActiveAreaButton && (
        activeArea ? (
          <div
            className="w-full p-3 bg-primary/10 border border-primary/20 rounded-lg relative flex items-center"
            data-active-area
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onHomeIconClick) {
                  onHomeIconClick();
                } else {
                  navigate('/running-list');
                }
              }}
              className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0 hover:bg-primary/30 transition-colors active:scale-95 z-10"
              title="Zur Gebietsübersicht"
            >
              <Home className="h-4 w-4 text-primary" />
            </button>
            <button
              onClick={() => onActiveAreaClick ? onActiveAreaClick() : navigate('/running-list')}
              className="absolute inset-0 flex items-center justify-center hover:opacity-80 transition-opacity active:scale-[0.99] pointer-events-auto"
            >
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">{activeArea.name}</p>
                <p className="text-xs text-muted-foreground">
                  {activeArea.plz}
                  {selectedLeadTypeLabel ? (
                    <span className="ml-1">• {selectedLeadTypeLabel}</span>
                  ) : (
                    ' • Aktives Gebiet'
                  )}
                </p>
              </div>
            </button>
          </div>
        ) : (
          <div className="w-full p-3 bg-muted/50 border border-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                  <Home className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">Gebietsübersicht</p>
                  <p className="text-xs text-muted-foreground">Kein Gebiet aktiv</p>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Lead Cards - memoized to prevent flickering */}
      <LeadCards
        globalLeadCounts={globalLeadCounts}
        isLoadingLeadCounts={isLoadingLeadCounts}
        selectedLeadType={selectedLeadType}
        onLeadCardClick={handleLeadCardClick}
      />
    </div>
  );
};
